import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
`;

const Box = styled.div`
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  padding: 40px 20px 32px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 32px;
  line-height: 1.4;
`;

const PrivacyContent = styled.div`
  height: 300px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  background: #fafafa;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 0;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &.agree {
    background: #000;
    color: #fff;
    &:disabled {
      background: #bdbdbd;
      cursor: not-allowed;
    }
  }

  &.cancel {
    background: #eee;
    color: #666;
  }
`;

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const privacyRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (privacyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = privacyRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setIsScrolledToBottom(true);
      }
    }
  };

  useEffect(() => {
    const privacyElement = privacyRef.current;
    if (privacyElement) {
      privacyElement.addEventListener('scroll', handleScroll);
      return () => privacyElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleAgree = () => {
    navigate('/signup/email', { state: location.state });
  };

  const handleCancel = () => {
    navigate('/login');
  };

  return (
    <Wrapper>
      <Box>
        <Title>개인정보보호정책</Title>
        <PrivacyContent ref={privacyRef}>
          <h3>1. 개인정보의 수집 및 이용 목적</h3>
          <p>회사는 다음의 목적을 위하여 개인정보를 처리하고 있으며, 다음의 목적 이외의 용도로는 이용하지 않습니다.</p>
          <p>- 회원 가입 및 관리</p>
          <p>- 서비스 제공 및 운영</p>
          <p>- 고객 상담 및 불만처리</p>
          
          <h3>2. 수집하는 개인정보의 항목</h3>
          <p>회사는 회원가입, 상담, 서비스 신청 등을 위해 아래와 같은 개인정보를 수집하고 있습니다.</p>
          <p>- 필수항목: 이름, 아이디, 비밀번호, 이메일</p>
          <p>- 선택항목: 전화번호, 주소</p>
          
          <h3>3. 개인정보의 보유 및 이용기간</h3>
          <p>회사는 원칙적으로 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다.</p>
          <p>단, 관계법령의 규정에 의하여 보존할 필요가 있는 경우 회사는 아래와 같이 관계법령에서 정한 일정한 기간 동안 회원정보를 보관합니다.</p>
          
          <h3>4. 개인정보의 파기절차 및 방법</h3>
          <p>회사는 원칙적으로 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체없이 파기합니다.</p>
          <p>파기절차 및 방법은 다음과 같습니다.</p>
          <p>- 파기절차: 회원이 서비스 가입 등을 위해 입력한 정보는 목적이 달성된 후 별도의 DB로 옮겨져 내부 방침 및 기타 관련 법령에 의한 정보보호 사유에 따라 일정 기간 저장된 후 파기됩니다.</p>
          <p>- 파기방법: 전자적 파일 형태로 저장된 개인정보는 기록을 재생할 수 없는 기술적 방법을 사용하여 삭제합니다.</p>
        </PrivacyContent>
        <ButtonGroup>
          <Button className="agree" onClick={handleAgree} disabled={!isScrolledToBottom}>
            동의
          </Button>
          <Button className="cancel" onClick={handleCancel}>
            취소
          </Button>
        </ButtonGroup>
      </Box>
    </Wrapper>
  );
};

export default PrivacyPage; 